import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  adminGetClient, adminUpdateClient, adminSetClientAccess, adminResendInvite,
  adminAddDeliverable, adminDeleteDeliverable, adminAddCohortMaterial,
  adminDeleteCohortMaterial, formatApiErrorDetail,
} from "@/lib/api";
import {
  ArrowLeft, Save, Copy, Check, Plus, Trash2, ShieldOff, ShieldCheck,
  Send, FileText, Film, ClipboardList, Presentation,
} from "lucide-react";
import { toast } from "sonner";

const TIERS = ["Sprint", "Accelerator", "Fractional", "Membership/The Table", "Other"];
const STATUSES = ["active", "paused", "completed"];
const DELIVERABLE_KINDS = ["roadmap", "audit", "recording", "doc"];
const MATERIAL_KINDS = ["deck", "recording", "worksheet", "doc"];
const DELIVERABLE_ICON = { roadmap: ClipboardList, audit: FileText, recording: Film, doc: FileText };

export default function AdminClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [eng, setEng] = useState(null);
  const [profile, setProfile] = useState({ name: "", company: "" });
  const [saving, setSaving] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const d = await adminGetClient(id);
    setData(d);
    setEng({ ...d.engagement });
    setProfile({ name: d.client.name || "", company: d.client.company || "" });
  }, [id]);

  useEffect(() => { load().catch(() => toast.error("Could not load client.")); }, [load]);

  if (!data || !eng) {
    return <p className="cv-body text-[#9A938A]" data-testid="client-detail-loading">Loading...</p>;
  }
  const c = data.client;

  const saveProfile = async () => {
    setSaving(true);
    try {
      await adminUpdateClient(id, {
        name: profile.name, company: profile.company,
        engagement_label: eng.label, tier: eng.tier, status: eng.status,
        engagement_summary: eng.summary, next_step: eng.next_step,
      });
      toast.success("Saved. The client's Hub updates immediately.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const toggleAccess = async (grant) => {
    try {
      await adminSetClientAccess(id, grant);
      toast.success(grant ? "Hub access granted." : "Hub access revoked. Record and deliverables are kept.");
      load();
    } catch (e) { toast.error("Could not change access."); }
  };

  const toggleTable = async (grant) => {
    try {
      await adminUpdateClient(id, { grant_table: grant });
      toast.success(grant ? "The Table granted." : "The Table removed.");
      load();
    } catch (e) { toast.error("Could not change membership."); }
  };

  const resend = async () => {
    try {
      const res = await adminResendInvite(id, window.location.origin);
      setInviteLink(res.welcome_link);
      toast.success("New invite link generated.");
    } catch (e) { toast.error("Could not generate invite."); }
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success("Invite link copied.");
  };

  return (
    <div data-testid="admin-client-detail">
      <Link to="/admin/clients" className="cv-link inline-flex items-center gap-1.5 text-xs">
        <ArrowLeft size={13} /> All clients
      </Link>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="cv-kicker">{c.company || "Client"}</p>
          <h1 className="cv-display text-3xl mt-3">{c.name || "(no name)"}</h1>
          <p className="cv-body text-sm text-[#9A938A] mt-2">{c.email}</p>
        </div>
        <div className="flex items-center gap-2">
          {!c.hub_access && <Badge tone="copper">Access revoked</Badge>}
          {c.membership === "table" && <Badge tone="sage">The Table</Badge>}
          {!c.has_password && <Badge tone="muted">Invite pending</Badge>}
        </div>
      </div>

      {/* Profile + engagement */}
      <Section title="Engagement" desc="Shown on the client's Hub dashboard as their active engagement and next step.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name">
            <input data-testid="detail-name" className="cv-input" value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
          </Field>
          <Field label="Company">
            <input data-testid="detail-company" className="cv-input" value={profile.company}
              onChange={(e) => setProfile({ ...profile, company: e.target.value })} />
          </Field>
          <Field label="Engagement label">
            <input data-testid="detail-engagement" className="cv-input" value={eng.label || ""}
              onChange={(e) => setEng({ ...eng, label: e.target.value })} />
          </Field>
          <Field label="Tier">
            <select data-testid="detail-tier" className="cv-input" value={eng.tier || "Other"}
              onChange={(e) => setEng({ ...eng, tier: e.target.value })}>
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select data-testid="detail-status" className="cv-input" value={eng.status || "active"}
              onChange={(e) => setEng({ ...eng, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Single next step (shown on dashboard)">
            <input data-testid="detail-next-step" className="cv-input" placeholder="e.g. Review your roadmap"
              value={eng.next_step || ""} onChange={(e) => setEng({ ...eng, next_step: e.target.value })} />
          </Field>
        </div>
        <Field label="Engagement summary">
          <textarea data-testid="detail-summary" rows={3} className="cv-input resize-none"
            placeholder="A short summary the client sees on their dashboard."
            value={eng.summary || ""} onChange={(e) => setEng({ ...eng, summary: e.target.value })} />
        </Field>
        <button data-testid="detail-save" onClick={saveProfile} disabled={saving}
          className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving..." : "Save engagement"}
        </button>
      </Section>

      {/* Access & membership */}
      <Section title="Access & membership" desc="Revoking access removes login only. The record and deliverables are kept.">
        <div className="flex flex-wrap items-center gap-3">
          {c.hub_access ? (
            <button data-testid="detail-revoke" onClick={() => toggleAccess(false)}
              className="cv-btn-secondary inline-flex items-center gap-2 text-[#A65A43] border-[#A65A43]/40">
              <ShieldOff size={14} /> Revoke Hub access
            </button>
          ) : (
            <button data-testid="detail-grant" onClick={() => toggleAccess(true)}
              className="cv-btn-secondary inline-flex items-center gap-2">
              <ShieldCheck size={14} /> Restore Hub access
            </button>
          )}
          {c.membership === "table" ? (
            <button data-testid="detail-table-off" onClick={() => toggleTable(false)} className="cv-btn-secondary">
              Remove The Table
            </button>
          ) : (
            <button data-testid="detail-table-on" onClick={() => toggleTable(true)} className="cv-btn-secondary">
              Grant The Table
            </button>
          )}
        </div>
      </Section>

      {/* Invite */}
      {!c.has_password && (
        <Section title="Login invite" desc="The client sets their password through this magic link. No payment required.">
          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="detail-resend" onClick={resend} className="cv-btn-secondary inline-flex items-center gap-2">
              <Send size={14} /> Generate invite link
            </button>
          </div>
          {inviteLink && (
            <div className="mt-3 flex items-stretch gap-2 max-w-2xl">
              <input readOnly data-testid="detail-invite-link" value={inviteLink} className="cv-input flex-1 text-xs" />
              <button onClick={() => copyLink(inviteLink)} className="cv-btn-secondary inline-flex items-center gap-2 whitespace-nowrap">
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          )}
        </Section>
      )}

      {/* Deliverables */}
      <Section title="Deliverables" desc="Roadmaps, audits, and recordings. These appear in the client's Hub > Deliverables.">
        <AddItem
          testid="deliverable"
          kinds={DELIVERABLE_KINDS}
          withDate
          onAdd={async (payload) => { await adminAddDeliverable(id, payload); toast.success("Deliverable added."); load(); }}
        />
        <ItemList
          items={data.deliverables}
          empty="No deliverables yet."
          iconMap={DELIVERABLE_ICON}
          onDelete={async (it) => { await adminDeleteDeliverable(id, it.id); toast.success("Removed."); load(); }}
          testid="deliverable-row"
        />
      </Section>

      {/* Cohort materials */}
      <Section title="Cohort materials" desc="Decks, recordings, worksheets. These appear in the client's Hub > Cohort Materials.">
        <AddItem
          testid="material"
          kinds={MATERIAL_KINDS}
          onAdd={async (payload) => { await adminAddCohortMaterial(id, payload); toast.success("Material added."); load(); }}
        />
        <ItemList
          items={data.cohort_materials}
          empty="No cohort materials yet."
          iconMap={{ deck: Presentation, recording: Film, worksheet: ClipboardList, doc: FileText }}
          onDelete={async (it) => { await adminDeleteCohortMaterial(id, it.id); toast.success("Removed."); load(); }}
          testid="material-row"
        />
      </Section>

      {/* Read-only context */}
      {(data.orders?.length > 0 || data.intake) && (
        <Section title="On record" desc="Read-only. Billing for Stripe activity stays in the customer portal.">
          {data.intake && (
            <div className="cv-body text-sm text-[#1F1B16]/70" data-testid="client-intake-on-record">
              {data.intake.responses?.length > 0 ? (
                <div className="space-y-2">
                  <p className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/55">Intake on file{data.intake.offer ? ` (${data.intake.offer})` : ""}</p>
                  {data.intake.responses.map((r, i) => (
                    <div key={i}>
                      <span className="font-medium text-[#1F1B16]">{r.question}: </span>
                      <span>{r.answer}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p>Intake submitted and on file.</p>
              )}
            </div>
          )}
          {data.orders?.map((o) => (
            <div key={o.id} className="flex items-center justify-between border-t border-[rgba(28,27,25,0.12)] py-2.5">
              <span className="cv-body text-sm">{o.product_key}</span>
              <span className="cv-serif text-sm text-[#1F1B16]/70">${o.amount}</span>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function AddItem({ kinds, withDate, onAdd, testid }) {
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState(kinds[0]);
  const [url, setUrl] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title is required."); return; }
    setBusy(true);
    try {
      const payload = { title, kind, url: url || null };
      if (withDate) payload.date = date || null;
      await onAdd(payload);
      setTitle(""); setUrl(""); setDate("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not add.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid={`add-${testid}-form`}
      className="border border-[rgba(28,27,25,0.16)] rounded-[14px] p-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
      <div className="sm:col-span-4">
        <Field label="Title">
          <input data-testid={`${testid}-title`} className="cv-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Kind">
          <select data-testid={`${testid}-kind`} className="cv-input" value={kind} onChange={(e) => setKind(e.target.value)}>
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
      </div>
      <div className={withDate ? "sm:col-span-3" : "sm:col-span-4"}>
        <Field label="Link (URL)">
          <input data-testid={`${testid}-url`} className="cv-input" placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} />
        </Field>
      </div>
      {withDate && (
        <div className="sm:col-span-2">
          <Field label="Date">
            <input data-testid={`${testid}-date`} type="date" className="cv-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
      )}
      <div className="sm:col-span-1">
        <button data-testid={`${testid}-add`} disabled={busy} className="cv-btn-primary w-full inline-flex items-center justify-center disabled:opacity-60">
          <Plus size={15} />
        </button>
      </div>
    </form>
  );
}

function ItemList({ items, empty, onDelete, iconMap, testid }) {
  if (!items?.length) {
    return <p className="cv-body text-sm text-[#9A938A] mt-4">{empty}</p>;
  }
  return (
    <div className="mt-4 space-y-2">
      {items.map((it) => {
        const Icon = (iconMap && iconMap[it.kind]) || FileText;
        return (
          <div key={it.id} data-testid={testid} className="border border-[rgba(28,27,25,0.14)] rounded-[12px] p-4 flex items-center gap-3">
            <Icon size={18} className="text-[#515E4F] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="cv-h2 text-sm truncate">{it.title}</p>
              <p className="cv-body text-xs text-[#9A938A] mt-0.5">
                {it.kind}{it.date ? ` · ${it.date}` : ""}{it.url ? " · linked" : " · no link yet"}
              </p>
            </div>
            {it.url && <a href={it.url} target="_blank" rel="noopener noreferrer" className="cv-link text-xs">Open</a>}
            <button onClick={() => onDelete(it)} className="text-[#A65A43] hover:text-[#8a4836] transition-colors" aria-label="Remove">
              <Trash2 size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <section className="mt-10 border-t border-[rgba(28,27,25,0.12)] pt-7">
      <h2 className="cv-h2 text-lg">{title}</h2>
      {desc && <p className="cv-body text-sm text-[#1F1B16]/60 mt-1.5 max-w-xl">{desc}</p>}
      <div className="mt-5 space-y-4 max-w-2xl">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Badge({ tone, children }) {
  const styles = {
    copper: "border-[#A65A43] text-[#A65A43]",
    sage: "border-[#515E4F] text-[#515E4F]",
    muted: "border-[rgba(28,27,25,0.3)] text-[#9A938A]",
  };
  return (
    <span className={`cv-sans uppercase tracking-[0.1em] text-[0.6rem] px-2.5 py-1 rounded-full border ${styles[tone]}`}>
      {children}
    </span>
  );
}
